/**
 * usePermission Hook
 * Hooks para verificación de permisos granulares en el frontend
 */

import { useAuth } from '../../components/auth-context';

import { PERMISSIONS as SHARED_PERMISSIONS, Permission as SharedPermission, SUPER_ADMIN_PERMISSION } from '../../../shared/permissions';

// Re-exportar para compatibilidad
export const PERMISSIONS = SHARED_PERMISSIONS;
export type Permission = SharedPermission;

/**
 * Hook para verificar si el usuario tiene un permiso específico
 * @param permission - El permiso a verificar
 * @returns true si el usuario tiene el permiso o es SuperAdmin
 */
export function usePermission(permission: Permission): boolean {
    const { user } = useAuth();

    if (!user) return false;

    // Si no tiene permissions definido, verificar isSuperAdmin como fallback
    if (!user.permissions) {
        return user.isSuperAdmin;
    }

    // SuperAdmin tiene acceso total
    if (user.permissions.includes(SUPER_ADMIN_PERMISSION)) {
        return true;
    }

    return user.permissions.includes(permission);
}

/**
 * Hook para verificar si el usuario tiene CUALQUIERA de los permisos
 * @param permissions - Array de permisos a verificar
 * @returns true si el usuario tiene al menos uno de los permisos
 */
export function useAnyPermission(permissions: Permission[]): boolean {
    const { user } = useAuth();

    if (!user) return false;

    if (!user.permissions) {
        return user.isSuperAdmin;
    }

    if (user.permissions.includes(SUPER_ADMIN_PERMISSION)) {
        return true;
    }

    return permissions.some(p => user.permissions?.includes(p));
}

/**
 * Hook para verificar si el usuario tiene TODOS los permisos
 * @param permissions - Array de permisos a verificar
 * @returns true si el usuario tiene todos los permisos
 */
export function useAllPermissions(permissions: Permission[]): boolean {
    const { user } = useAuth();

    if (!user) return false;

    if (!user.permissions) {
        return user.isSuperAdmin;
    }

    if (user.permissions.includes(SUPER_ADMIN_PERMISSION)) {
        return true;
    }

    return permissions.every(p => user.permissions?.includes(p));
}

/**
 * Función helper para verificar permisos fuera de componentes
 * @param userPermissions - Array de permisos del usuario
 * @param permission - Permiso a verificar
 */
export function hasPermission(userPermissions: string[] | undefined, permission: Permission): boolean {
    if (!userPermissions) return false;
    if (userPermissions.includes(SUPER_ADMIN_PERMISSION)) return true;
    return userPermissions.includes(permission);
}
